import { useMemo, useRef, useState } from 'react';
import { Paperclip, Pencil, Send, Trash2, X } from 'lucide-react';
import { CommentAttachments } from '../../ui/CommentAttachments';
import { useConfirm } from '../../ui/ConfirmProvider';
import { useAuthStore } from '../../../store/useAuthStore';
import {
  canEditComment,
  canRemoveComment,
  removeAttachmentPrompt,
  removeCommentPrompt,
} from '../../../lib/commentRemoval';
import type {
  LeadActivity,
  LeadComment,
  LeadCommentAttachment,
  LeadTimelineItem,
} from '../../../lib/leadsApi';
import type { AuthUser } from '../../../lib/authApi';
import { uploadLeadFile } from '../../../lib/leadsApi';
import { useUploadQueue } from '../../../hooks/useUploadQueue';
import { UploadChipList } from '../../ui/UploadProgress';
import { RelativeTime } from '../../ui/RelativeTime';
import { useUsers } from '../../../hooks/useUsers';
import {
  useAddLeadComment,
  useDeleteLeadComment,
  useDeleteLeadCommentAttachment,
  useLeadTimeline,
  useUpdateLeadComment,
} from '../../../hooks/useLeads';
import { Avatar, MeAvatar } from '../../tasks/linear/Avatar';
import { MarkdownView } from '../../tasks/linear/MarkdownView';

/**
 * CLE-192 — unified activity + comments timeline for the lead detail page.
 *
 * The timeline (comments + auto-logged activity) renders chronologically with
 * avatars, actor names, action text and relative timestamps. A composer at the
 * bottom posts markdown comments with optional attachments (paperclip button +
 * drag/paste) and @mention support. Only the author edits a comment inline or
 * deletes it, or a single file from it, after a confirm.
 */

function prettyEnum(v: string): string {
  return v
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Map a lead activity `field` to a human-readable phrase.
function activityText(a: LeadActivity): string {
  const who = a.user?.name ?? 'Cineva';
  switch (a.field) {
    case 'created':
      return `${who} a creat lead-ul`;
    case 'comment_added':
      return `${who} a comentat`;
    // Logged interactions (CLE-261): email / call / meeting / chat message.
    case 'interaction_email':
      return a.newValue ? `${who}: email — ${a.newValue}` : `${who} a trimis un email`;
    case 'interaction_call':
      return a.newValue ? `${who}: apel telefonic — ${a.newValue}` : `${who} a sunat lead-ul`;
    case 'interaction_meeting':
      return a.newValue ? `${who}: întâlnire — ${a.newValue}` : `${who} a avut o întâlnire`;
    case 'interaction_message':
      return a.newValue
        ? `${who}: mesaj WhatsApp/chat — ${a.newValue}`
        : `${who} a trimis un mesaj`;
    case 'stageId':
      return `${who} a mutat lead-ul într-o nouă etapă`;
    case 'temperature':
      return `${who} a setat temperatura${a.newValue ? ` la ${prettyEnum(a.newValue)}` : ''}`;
    case 'source':
      return `${who} a schimbat sursa${a.newValue ? ` în ${prettyEnum(a.newValue)}` : ''}`;
    case 'sourceLabel':
      return a.newValue
        ? `${who} a schimbat sursa în ${a.newValue}`
        : `${who} a scos sursa personalizată`;
    case 'assigneeId':
      return a.newValue
        ? `${who} a schimbat responsabilul`
        : `${who} a eliminat responsabilul lead-ului`;
    case 'value':
      return a.newValue ? `${who} a actualizat valoarea` : `${who} a șters valoarea`;
    case 'currency':
      return `${who} a schimbat moneda`;
    case 'expectedCloseDate':
      return a.newValue
        ? `${who} a setat data estimată de închidere`
        : `${who} a șters data de închidere`;
    case 'lastContactedAt':
      return `${who} a actualizat ultimul contact`;
    case 'title':
      return `${who} a redenumit lead-ul`;
    case 'description':
      return `${who} a actualizat descrierea`;
    case 'clientId':
      return a.newValue ? `${who} a asociat un client` : `${who} a eliminat clientul`;
    case 'contactName':
    case 'contactEmail':
    case 'contactPhone':
    case 'companyName':
      return `${who} a actualizat detaliile de contact`;
    case 'label_added':
      return `${who} a adăugat o etichetă`;
    case 'label_removed':
      return `${who} a eliminat o etichetă`;
    case 'action_created':
      return `${who} a adăugat o acțiune`;
    case 'action_completed':
      return `${who} a finalizat o acțiune`;
    case 'converted':
      return `${who} a convertit lead-ul într-un proiect`;
    // The WON rule (services/leadWon.ts).
    case 'won':
      return `${who} a câștigat lead-ul${a.newValue ? ` (${a.newValue})` : ''}`;
    case 'client_created':
      return 'Lead-ul a devenit client: clientul a fost creat automat';
    case 'client_linked':
      return 'Lead-ul a fost asociat automat clientului existent';
    case 'pipelineId':
      return 'Lead-ul a trecut automat în pipeline-ul de onboarding';
    case 'archived':
      return `${who} a arhivat lead-ul`;
    default:
      return `${who} a actualizat ${a.field.replace(/([A-Z])/g, ' $1').toLowerCase()}`;
  }
}

export function LeadDetailActivity({ leadId }: { leadId: string }) {
  const { data: timeline, isLoading } = useLeadTimeline(leadId);
  const deleteComment = useDeleteLeadComment(leadId);
  const deleteAttachment = useDeleteLeadCommentAttachment(leadId);
  const updateComment = useUpdateLeadComment(leadId);
  const confirm = useConfirm();
  const viewer = useAuthStore((s) => s.user);
  const removeError = deleteComment.error ?? deleteAttachment.error;

  async function removeComment(comment: LeadComment) {
    if (await confirm(removeCommentPrompt(comment))) deleteComment.mutate(comment.id);
  }

  async function removeAttachment(comment: LeadComment, index: number, name: string) {
    if (await confirm(removeAttachmentPrompt(comment, name))) {
      deleteAttachment.mutate({ commentId: comment.id, index, name });
    }
  }

  const items = timeline ?? [];

  return (
    <section className="mt-8">
      <h2 className="mb-3 text-[13px] font-semibold text-gray-500">Activitate</h2>

      {isLoading ? (
        <p className="text-[13px] text-gray-400">Se încarcă activitatea…</p>
      ) : items.length === 0 ? (
        <p className="text-[13px] text-gray-400">Încă nicio activitate.</p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <TimelineRow
              key={`${item.kind}-${item.data.id}`}
              item={item}
              viewer={viewer}
              onDeleteComment={(c) => void removeComment(c)}
              onRemoveAttachment={(c, index, name) => void removeAttachment(c, index, name)}
              onEditComment={(id, content) => updateComment.mutate({ commentId: id, content })}
            />
          ))}
        </ul>
      )}
      {removeError && (
        <p className="mt-2 text-[12px] text-red-600">Nu s-a putut șterge: {removeError.message}</p>
      )}

      <CommentComposer leadId={leadId} />
    </section>
  );
}

function TimelineRow({
  item,
  viewer,
  onDeleteComment,
  onRemoveAttachment,
  onEditComment,
}: {
  item: LeadTimelineItem;
  viewer: AuthUser | null;
  onDeleteComment: (comment: LeadComment) => void;
  onRemoveAttachment: (comment: LeadComment, index: number, name: string) => void;
  onEditComment: (id: string, content: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (item.kind === 'activity') {
    const a = item.data;
    return (
      <li className="flex items-center gap-2 pl-1 text-[12.5px] text-gray-400">
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
        <span>{activityText(a)}</span>
        <span className="text-gray-300">·</span>
        <RelativeTime iso={a.createdAt} />
      </li>
    );
  }

  const c = item.data;
  const canEdit = canEditComment(viewer, c.authorId);
  const canRemove = canRemoveComment(viewer, c.authorId);

  function commitEdit() {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== c.content) onEditComment(c.id, next);
  }

  return (
    <li className="flex gap-2.5">
      <Avatar name={c.author?.name ?? null} size={26} />
      <div className="min-w-0 flex-1 rounded-md border border-gray-200 bg-card px-3 py-2">
        <div className="mb-1 flex items-center justify-between">
          <span className="text-[13px] font-medium text-gray-800">
            {c.author?.name ?? 'Necunoscut'}
          </span>
          <div className="flex items-center gap-2">
            <RelativeTime iso={c.createdAt} className="text-[11px] text-gray-400" />
            {canEdit && !editing && (
              <button
                type="button"
                onClick={() => {
                  setDraft(c.content);
                  setEditing(true);
                }}
                className="text-gray-400 hover:text-gray-800"
                aria-label="Editează comentariul"
              >
                <Pencil size={13} />
              </button>
            )}
            {canRemove && (
              <button
                type="button"
                onClick={() => onDeleteComment(c)}
                className="text-gray-400 hover:text-red-500"
                title="Șterge comentariul"
                aria-label="Șterge comentariul"
              >
                <Trash2 size={13} />
              </button>
            )}
          </div>
        </div>
        {editing ? (
          <div>
            <textarea
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setEditing(false);
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  commitEdit();
                }
              }}
              rows={Math.max(2, draft.split('\n').length)}
              className="block w-full resize-y rounded border border-gray-200 bg-transparent px-2 py-1.5 text-[13px] text-gray-800 focus:border-brand-600 focus:outline-none"
            />
            <div className="mt-1.5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(false)}
                className="rounded px-2 py-0.5 text-[12px] text-gray-500 hover:bg-gray-200"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={commitEdit}
                className="rounded bg-brand-600 px-2.5 py-0.5 text-[12px] font-medium text-fixed-white hover:bg-brand-700"
              >
                Salvează
              </button>
            </div>
          </div>
        ) : (
          <>
            {c.content && <MarkdownView content={c.content} />}
            <CommentAttachments
              attachments={c.attachments ?? []}
              onRemove={canRemove ? (index, a) => onRemoveAttachment(c, index, a.name) : undefined}
            />
          </>
        )}
      </div>
    </li>
  );
}

function CommentComposer({ leadId }: { leadId: string }) {
  const addComment = useAddLeadComment(leadId);
  const { data: users } = useUsers();
  const [value, setValue] = useState('');
  const [pending, setPending] = useState<LeadCommentAttachment[]>([]);
  // One chip per in-flight file, each with its own bar.
  const { uploads, run } = useUploadQueue();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const suggestions = useMemo(() => {
    if (mentionQuery == null || !users) return [];
    return users.filter((u) => u.name.toLowerCase().includes(mentionQuery)).slice(0, 6);
  }, [mentionQuery, users]);

  function onChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const text = e.target.value;
    setValue(text);
    const caret = e.target.selectionStart ?? text.length;
    const match = /@([\w.-]*)$/.exec(text.slice(0, caret));
    setMentionQuery(match ? match[1].toLowerCase() : null);
  }

  function applyMention(name: string) {
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? value.length;
    const before = value.slice(0, caret).replace(/@([\w.-]*)$/, `@${name} `);
    setValue(before + value.slice(caret));
    setMentionQuery(null);
    el?.focus();
  }

  async function handleFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploadError(null);
    try {
      for (const f of list) {
        const uploaded = await run(f, (onProgress) => uploadLeadFile(leadId, f, onProgress));
        // Chip by chip, so a finished file is attached and visible while the
        // rest of the batch is still uploading.
        setPending((prev) => [...prev, uploaded]);
      }
    } catch (err) {
      // A failed upload used to be swallowed here: the spinner stopped, no chip
      // appeared and nothing said why. Show the reason instead.
      setUploadError(err instanceof Error ? err.message : 'Încărcarea fișierului a eșuat.');
    }
  }

  function submit() {
    const content = value.trim();
    if (!content && pending.length === 0) return;
    addComment.mutate(
      { content, attachments: pending.length ? pending : undefined },
      {
        onSuccess: () => {
          setValue('');
          setPending([]);
          setMentionQuery(null);
        },
      },
    );
  }

  return (
    <div className="mt-5 flex gap-2.5">
      <div className="hidden sm:block">
        <MeAvatar size={26} />
      </div>
      <div
        className="relative flex-1 rounded-md border border-gray-200 bg-card"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (e.dataTransfer.files.length) void handleFiles(e.dataTransfer.files);
        }}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={onChange}
          onPaste={(e) => {
            const imgs = Array.from(e.clipboardData.items).filter((it) =>
              it.type.startsWith('image/'),
            );
            if (imgs.length) {
              e.preventDefault();
              void handleFiles(imgs.map((it) => it.getAsFile()).filter((f): f is File => !!f));
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              submit();
            }
          }}
          rows={2}
          placeholder="Lasă un comentariu…"
          className="block w-full resize-y bg-transparent px-3 py-2 text-[13px] leading-relaxed text-gray-800 placeholder:text-gray-400 focus:outline-none"
        />

        {uploadError && (
          <p className="px-3 pb-2 text-[11px] leading-relaxed text-red-500">{uploadError}</p>
        )}

        {(pending.length > 0 || uploads.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
            <UploadChipList uploads={uploads} />
            {pending.map((a, i) => (
              <span
                key={`${a.name}-${i}`}
                className="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-500"
              >
                <Paperclip size={10} /> {a.name}
                <button
                  type="button"
                  onClick={() => setPending((p) => p.filter((_, idx) => idx !== i))}
                  className="text-gray-400 hover:text-red-500"
                  aria-label="Elimină atașamentul"
                >
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
        )}

        {suggestions.length > 0 && (
          <ul className="absolute bottom-full left-2 z-10 mb-1 max-h-44 w-56 overflow-auto rounded-md border border-gray-200 bg-gray-200 py-1 shadow-lg">
            {suggestions.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => applyMention(u.name)}
                  className="flex w-full items-center px-3 py-1.5 text-left text-[13px] text-gray-700 hover:bg-gray-200"
                >
                  {u.name}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex items-center justify-between border-t border-gray-200 px-2 py-1.5">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1 rounded px-1.5 py-1 text-[12px] text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            title="Atașează o poză sau un fișier"
          >
            <Paperclip size={14} />
          </button>
          <input
            ref={fileRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              if (e.target.files?.length) void handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            onClick={submit}
            disabled={addComment.isPending || (!value.trim() && pending.length === 0)}
            className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-2.5 py-1 text-[12px] font-medium text-fixed-white hover:bg-brand-700 disabled:opacity-40"
          >
            <Send size={12} /> {addComment.isPending ? 'Se trimite…' : 'Comentează'}
          </button>
        </div>
      </div>
    </div>
  );
}
